import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { toast } from "sonner";
import { notifyResearch } from "@/hooks/useNotifications";

export default function ResearchReviewers({ itemId, itemTitle, authorId }: { itemId: string; itemTitle: string; authorId: string | null }) {
  const qc = useQueryClient();
  const [reviewerId, setReviewerId] = useState("");
  const [outcomeNote, setOutcomeNote] = useState("");

  const { data: reviewers = [] } = useQuery({
    queryKey: ["potential_reviewers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, staff_members(first_name, last_name)")
        .or("team.eq.Investment,role_level.in.(admin,superadmin)");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: assignments = [] } = useQuery({
    queryKey: ["research_review_assignments", itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_review_assignments")
        .select("*")
        .eq("item_id", itemId)
        .order("created_at", { ascending: false } as any);
      if (error) throw error;
      return data ?? [];
    },
  });

  const assign = useMutation({
    mutationFn: async () => {
      if (!reviewerId) throw new Error("Select a reviewer");
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("research_review_assignments").insert({
        item_id: itemId, reviewer_id: reviewerId, role: "reviewer", assigned_by: u.user?.id,
      });
      if (error) throw error;
      await notifyResearch({
        user_ids: [reviewerId],
        category: "research.review_request",
        title: `Review requested: "${itemTitle}"`,
        message: "You have been assigned as a reviewer.",
        link_url: `/partner/research/${itemId}`,
        data: { item_id: itemId },
      });
    },
    onSuccess: () => {
      setReviewerId("");
      qc.invalidateQueries({ queryKey: ["research_review_assignments", itemId] });
      toast.success("Reviewer assigned");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const submitReview = useMutation({
    mutationFn: async ({ assignmentId, outcome }: { assignmentId: string; outcome: "approved" | "changes_requested" | "rejected" }) => {
      const { error } = await supabase
        .from("research_review_assignments")
        .update({ outcome, outcome_note: outcomeNote || null, completed_at: new Date().toISOString() } as any)
        .eq("id", assignmentId);
      if (error) throw error;
      if (authorId) {
        await notifyResearch({
          user_ids: [authorId],
          category: `research.review_${outcome}`,
          title: `Review ${outcome.replace("_", " ")}: "${itemTitle}"`,
          message: outcomeNote || undefined,
          link_url: `/partner/research/${itemId}`,
          data: { item_id: itemId },
        });
      }
    },
    onSuccess: () => {
      setOutcomeNote("");
      qc.invalidateQueries({ queryKey: ["research_review_assignments", itemId] });
      toast.success("Review submitted");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const nameFor = (uid: string) => {
    const r = reviewers.find((x) => x.user_id === uid);
    const s = r?.staff_members;
    return s ? `${s.first_name} ${s.last_name}` : uid.slice(0, 8);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="font-semibold text-sm">Reviewers ({assignments.length})</div>

        <div className="flex gap-2">
          <Select value={reviewerId} onValueChange={setReviewerId}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="Assign reviewer…" /></SelectTrigger>
            <SelectContent>
              {reviewers.map((r: any) => (
                <SelectItem key={r.user_id} value={r.user_id}>
                  {r.staff_members ? `${r.staff_members.first_name} ${r.staff_members.last_name}` : r.user_id.slice(0, 8)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => assign.mutate()} disabled={!reviewerId || assign.isPending}>Assign</Button>
        </div>

        <div className="space-y-2">
          {assignments.map((a: any) => (
            <div key={a.id} className="border rounded p-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="font-medium">{nameFor(a.reviewer_id)}</div>
                <Badge variant={a.outcome === "approved" ? "default" : a.outcome === "pending" ? "outline" : "secondary"}>
                  {a.outcome}
                </Badge>
              </div>
              {a.outcome_note && <div className="text-xs text-muted-foreground mt-1">{a.outcome_note}</div>}
              <div className="text-[10px] text-muted-foreground mt-1">
                Assigned {format(new Date(a.created_at), "dd/MM/yyyy")}
                {a.completed_at && ` · Completed ${format(new Date(a.completed_at), "dd/MM/yyyy")}`}
              </div>
              {a.outcome === "pending" && (
                <div className="mt-2 space-y-2">
                  <Textarea value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} placeholder="Review notes (optional)" rows={2} />
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => submitReview.mutate({ assignmentId: a.id, outcome: "approved" })}>Approve</Button>
                    <Button size="sm" variant="secondary" onClick={() => submitReview.mutate({ assignmentId: a.id, outcome: "changes_requested" })}>Request changes</Button>
                    <Button size="sm" variant="destructive" onClick={() => submitReview.mutate({ assignmentId: a.id, outcome: "rejected" })}>Reject</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
